import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import UsageWarnings from "@/components/UsageWarnings";
import NotificationBell from "@/components/NotificationBell";
import {
  Home, Video, Sparkles, Users, Zap, Video as ZoomIcon,
  CreditCard, FileText, Settings, LogOut, BarChart3, UserPlus, Globe2, Gem, Layers,
  LayoutPanelLeft, Coins, Route, Wallet, ShieldCheck, Crown, Palette,
  Presentation, UserCog, Target, UserCheck, Package,
} from "lucide-react";

// Grouped nav — makes 21 items scannable by role
const NAV_GROUPS = [
  {
    label: null,
    items: [
      { to: "/app", icon: Home, label: "Home", end: true },
    ],
  },
  {
    label: "Create",
    items: [
      { to: "/app/webinars", icon: Video, label: "Webinars" },
      { to: "/app/courses", icon: Layers, label: "Courses" },
      { to: "/app/pages", icon: LayoutPanelLeft, label: "Landing Pages" },
      { to: "/app/funnels", icon: Route, label: "Funnels" },
      { to: "/app/presentations", icon: Presentation, label: "AI Presentations" },
      { to: "/app/studio", icon: Palette, label: "Thumbnail Studio" },
      { to: "/app/designs", icon: LayoutPanelLeft, label: "Design Studio" },
      { to: "/app/ai", icon: Sparkles, label: "AI Content Studio" },
      { to: "/app/brand-kits", icon: Layers, label: "Brand Kits" },
    ],
  },
  {
    label: "Grow",
    items: [
      { to: "/app/registrants", icon: Users, label: "Registrants" },
      { to: "/app/automations", icon: Zap, label: "Automations" },
      { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
      { to: "/app/team", icon: UserPlus, label: "Team" },
    ],
  },
  {
    label: "CRM",
    items: [
      { to: "/app/crm/leads",    icon: Target,     label: "Leads" },
      { to: "/app/crm/clients",  icon: UserCheck,  label: "Clients" },
      { to: "/app/crm/services", icon: Package,    label: "Services" },
      { to: "/app/crm/settings", icon: Settings,   label: "CRM Settings" },
    ],
  },
  {
    label: "Business",
    items: [
      { to: "/app/plans", icon: Gem, label: "Plans & Billing" },
      { to: "/app/credits", icon: Coins, label: "Credits" },
      { to: "/app/payouts", icon: Wallet, label: "Payouts" },
      { to: "/app/domains", icon: Globe2, label: "Custom Domains" },
      { to: "/app/zoom", icon: ZoomIcon, label: "Zoom" },
      { to: "/app/payments", icon: CreditCard, label: "Payments" },
      { to: "/app/templates", icon: FileText, label: "Templates" },
      { to: "/app/settings", icon: Settings, label: "Settings" },
    ],
  },
];

const ADMIN_NAV = [
  { to: "/app/admin/onboarding", icon: UserCog, label: "Client Onboarding" },
  { to: "/app/admin/lifetime-grants", icon: Crown, label: "Lifetime Grants" },
  { to: "/app/admin/payouts", icon: ShieldCheck, label: "Payouts Admin" },
];

// Mobile bottom nav — 5 most-used items only
const MOBILE_QUICK = [
  { to: "/app", icon: Home, label: "Home", end: true },
  { to: "/app/webinars", icon: Video, label: "Webinars" },
  { to: "/app/registrants", icon: Users, label: "Regs" },
  { to: "/app/analytics", icon: BarChart3, label: "Stats" },
  { to: "/app/settings", icon: Settings, label: "Settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user?.is_admin;

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="vp-shell flex min-h-screen">
      {/* Sidebar (desktop) — dark navy, brand-consistent */}
      <aside
        className="vp-sidebar hidden md:flex md:flex-col w-[260px] shrink-0 p-4 gap-0.5 sticky top-0 h-screen overflow-y-auto"
        data-testid="dashboard-sidebar"
      >
        <div className="flex items-center gap-2.5 mb-4 px-2 pt-1">
          <div className="w-9 h-9 rounded-xl bg-[#0B5CFF] flex items-center justify-center shadow-[0_4px_12px_rgba(11,92,255,0.35)]">
            <Video className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-black text-lg leading-none vp-sidebar-brand-title">Videoepreneur</div>
            <div className="vp-sidebar-brand-sub text-[0.65rem] tracking-widest uppercase font-bold mt-1">Webinar OS</div>
          </div>
        </div>

        <nav className="flex flex-col gap-0.5 flex-1">
          {isAdmin && (
            <div className="mb-2 pb-2 border-b border-white/8">
              <div className="vp-sidebar-group flex items-center gap-1.5" style={{ color: "#F59E0B" }}>
                <Crown className="w-3 h-3" strokeWidth={2.5} />
                <span>Founder</span>
              </div>
              {ADMIN_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => "vp-nav-item " + (isActive ? "active" : "")}
                  style={({ isActive }) => (isActive ? { color: "#F59E0B" } : undefined)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon className="w-4 h-4" strokeWidth={1.75} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          )}
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className="flex flex-col gap-0.5">
              {group.label && <div className="vp-sidebar-group">{group.label}</div>}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => "vp-nav-item " + (isActive ? "active" : "")}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon className="w-4 h-4" strokeWidth={1.75} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="vp-sidebar-footer mt-3">
          <div className="flex items-center justify-between px-1 gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate vp-user-name">{user?.name || user?.email}</div>
              <div className="text-xs truncate vp-user-mail">{user?.email}</div>
            </div>
            <NotificationBell />
            <button
              className="p-2 rounded-lg transition-colors"
              onClick={onLogout}
              data-testid={TIDS.logoutBtn}
              title="Logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#050B24] text-white px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0B5CFF] flex items-center justify-center">
            <Video className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div className="font-display font-black">Videoepreneur</div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button className="text-white/80" onClick={onLogout} data-testid={TIDS.logoutBtn + "-mobile"}>
            <LogOut className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <ImpersonationBanner />
        <UsageWarnings />
        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-3 inset-x-3 z-40 bg-[#050B24] text-white rounded-2xl px-2 py-2 flex justify-between overflow-x-auto shadow-2xl border border-white/10">
          {MOBILE_QUICK.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                "flex flex-col items-center px-3 py-1 rounded-xl transition-colors " +
                (isActive ? "text-white bg-[#0B5CFF]" : "text-white/60")
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              <span className="text-[10px] mt-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
        <div data-testid={TIDS.dashRoot} className="p-5 sm:p-8 lg:p-10 pb-28 md:pb-10 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
